import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { z } from 'zod';
import { AccountingPackForm } from '../../../components/AccountingPack';
import { Alert } from '../../../components/States';
import { Combobox } from '../../../components/Combobox';
import { productOption } from '../../../components/pickers';
import { useToast } from '../../../components/Toast';
import { API_BASE, csrfHeaders, newIdempotencyKey } from '../../../lib/api';
import { errorMessage } from '../../../lib/messages';
import { useProducts } from '../../../lib/products-api';
import { useIsAdmin } from '../../../lib/session';

const CSVS: [string, string][] = [
  ['Produtos e variações', 'produtos'],
  ['Preços por canal', 'precos-por-canal'],
  ['Orçamentos', 'orcamentos'],
  ['Pedidos', 'pedidos'],
  ['Clientes', 'clientes'],
  ['Impressões (fila)', 'impressoes'],
  ['Filamentos', 'filamentos'],
  ['Carretéis', 'carreteis'],
  ['Insumos', 'insumos'],
  ['Despesas', 'despesas'],
];

/** "Exportar" (board Dados-Exportar). */
export function ExportTab() {
  const isAdmin = useIsAdmin();
  return (
    <div
      style={{
        display: 'grid',
        gap: 18,
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))',
        alignItems: 'start',
      }}
    >
      <section className="card flat">
        <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 8 }}>
          <div>
            <h2>Listas em CSV</h2>
            <p className="xs muted">
              ponto e vírgula, vírgula decimal, UTF-8 com BOM (abre direto no Excel)
            </p>
          </div>
          {isAdmin ? null : <span className="sm muted">Só Admin exporta</span>}
        </div>
        <div className="gt">
          {CSVS.map(([name, file]) => (
            <div key={file} className="gtr" style={{ gridTemplateColumns: '1fr auto' }}>
              <span>
                {name}
                <span className="xs muted" style={{ display: 'block' }}>
                  {file}.csv
                </span>
              </span>
              {isAdmin ? (
                <a className="btn sm" href={`/api/v1/exports/${file}.csv`} download>
                  Baixar CSV
                </a>
              ) : (
                <button type="button" className="btn sm" disabled>
                  Baixar CSV
                </button>
              )}
            </div>
          ))}
        </div>
        <p className="xs muted" style={{ padding: '10px 18px 16px' }}>
          Nas telas de pedidos, orçamentos, produtos e clientes, “Exportar CSV” leva os filtros que
          estiverem aplicados.
        </p>
      </section>
      <div className="col" style={{ gap: 18 }}>
        <section className="card col" style={{ gap: 10 }}>
          <div className="row between">
            <h2>Pacote do contador</h2>
            {isAdmin ? null : <span className="sm muted">Só Admin gera</span>}
          </div>
          <p className="sm muted">
            Vendas, despesas, notas e recibos do mês em um .zip, com o resultado e o relatório de
            receitas brutas em PDF.
          </p>
          {isAdmin ? <AccountingPackForm /> : null}
        </section>
        <ProductExportCard isAdmin={isAdmin} />
        {isAdmin ? <ProductImportCard /> : null}
      </div>
    </div>
  );
}

function ProductExportCard({ isAdmin }: { isAdmin: boolean }) {
  const list = useProducts({ segment: 'all', category: '', material: '' });
  const [id, setId] = useState('');
  const [withFiles, setWithFiles] = useState(true);
  const products = list.data?.data ?? [];
  const chosen = id || products[0]?.id || '';
  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row between">
        <h2>Exportar produto completo</h2>
        {isAdmin ? null : <span className="sm muted">Só Admin exporta</span>}
      </div>
      <p className="sm muted">
        Dados, variações, preços por canal e arquivos, para levar a outro sistema ou guardar.
      </p>
      <div className="field">
        <label htmlFor="bundle-product">Produto</label>
        <Combobox
          id="bundle-product"
          value={chosen}
          disabled={!isAdmin || products.length === 0}
          options={products.map(productOption)}
          onChange={setId}
        />
      </div>
      <button
        type="button"
        className="check"
        role="checkbox"
        aria-checked={withFiles}
        disabled={!isAdmin}
        onClick={() => setWithFiles(!withFiles)}
      >
        <span className={withFiles ? 'box on' : 'box'} aria-hidden="true">
          {withFiles ? '✓' : ''}
        </span>
        Incluir arquivos (3MF, fotos e vídeos)
      </button>
      {isAdmin && chosen ? (
        <a
          className="btn"
          href={`/api/v1/products/${chosen}/bundle.zip?files=${withFiles ? 'true' : 'false'}`}
          download
        >
          Exportar {products.find((p) => p.id === chosen)?.name ?? 'produto'}
        </a>
      ) : (
        <button type="button" className="btn" disabled>
          Exportar produto
        </button>
      )}
    </section>
  );
}

const ImportResult = z.object({
  product: z.object({ id: z.string(), name: z.string(), sku: z.string() }),
  warnings: z.array(z.string()),
});

async function uploadBundle(file: File, key: string): Promise<z.infer<typeof ImportResult>> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await fetch(`${API_BASE}/products/bundle`, {
    method: 'POST',
    credentials: 'include',
    headers: { accept: 'application/json', 'idempotency-key': key, ...csrfHeaders() },
    body: form,
  });
  const json: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const err = z.object({ error: z.object({ message: z.string() }) }).safeParse(json);
    throw new Error(err.success ? err.data.error.message : `HTTP ${res.status}`);
  }
  return ImportResult.parse(json);
}

/** "Importar produto": a .zip exported by Deizi3d becomes a new product. */
function ProductImportCard() {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [key, setKey] = useState(newIdempotencyKey);
  const imp = useMutation({
    mutationFn: (f: File) => uploadBundle(f, key),
    onSuccess: (r) => {
      setKey(newIdempotencyKey());
      toast(`${r.product.name} importado como ${r.product.sku}`);
    },
  });
  return (
    <section className="card col" style={{ gap: 10 }}>
      <h2>Importar produto</h2>
      <p className="sm muted">
        Um .zip exportado pelo Deizi3d vira um produto novo. Impressoras, filamentos, insumos, kits
        e canais são ligados pelo nome; o que não existir aqui fica de fora e aparece na lista.
      </p>
      <input
        ref={input}
        type="file"
        hidden
        accept=".zip"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) imp.mutate(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        className="btn"
        style={{ alignSelf: 'flex-start' }}
        disabled={imp.isPending}
        onClick={() => input.current?.click()}
      >
        {imp.isPending ? 'Importando…' : 'Escolher .zip do produto'}
      </button>
      {imp.isError ? <Alert tone="err">{errorMessage(imp.error)}</Alert> : null}
      {imp.data ? (
        <div className="alert ok">
          <div>
            <b>
              {imp.data.product.name} importado ({imp.data.product.sku}).
            </b>{' '}
            <Link to={`/produtos/${imp.data.product.id}`}>Abrir produto</Link>
            {imp.data.warnings.length ? (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {imp.data.warnings.map((w) => (
                  <li key={w} className="sm">
                    {w}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
